"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "STEP 01",
  title: "INITIAL CONSULTATION",
  description: "We begin with a private meeting to review your legal needs. We listen to your objectives, analyze the core facts, and determine how our firm can best serve your specific litigation or business goals.",
  timeframe: "PHASE ONE"
}, {
  number: "STEP 02",
  title: "STRATEGIC ASSESSMENT",
  description: "Our litigation team conducts a rigorous review of documents, contracts, and dispute history. We identify core liabilities, exposure risks, and leverage points to craft a precise, high-value strategy.",
  timeframe: "PHASE TWO"
}, {
  number: "STEP 03",
  title: "RESOLUTION OR TRIAL",
  description: "Whether through strategic mediation, structured settlement negotiations, or courtroom advocacy in South Florida courts, we pursue a definitive and advantageous resolution with unwavering preparedness.",
  timeframe: "PHASE THREE"
}];
function ProcessStep({
  step,
  index,
  isLast
}: {
  step: (typeof steps)[number];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-[50px_1fr] md:grid-cols-[100px_1fr] gap-6 md:gap-16 ${isLast ? "" : "mb-16 md:mb-24"} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      {}
      <div className="relative flex justify-start md:justify-center pt-2">
        <div className="relative flex items-center justify-center">
          <div className={`w-4 h-4 rounded-lg transition-all duration-500 bg-card border-2 ${isVisible ? "border-primary bg-primary shadow-md scale-110" : "border-border bg-background"}`} />
        </div>
      </div>

      {}
      <div className="min-w-0">
        <div className="mb-3">
          <span className="font-mono text-xs tracking-widest text-primary font-medium">
            {step.number}
          </span>
        </div>

        <div className="bg-card p-8 md:p-10 rounded-lg shadow-md border border-border transition-all duration-300 hover:shadow-lg">
          <h3 className="font-serif font-bold text-2xl md:text-3xl tracking-tight text-card-foreground mb-4">
            {step.title}
          </h3>

          <div className="w-12 h-px bg-secondary/50 mb-6" />

          <p className="font-sans text-base md:text-lg leading-relaxed text-muted-foreground max-w-[640px] mb-6">
            {step.description}
          </p>

          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-secondary rounded-lg" />
            <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
              {step.timeframe}
            </span>
          </div>
        </div>
      </div>
    </div>;
}
export default function Theapproach() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setLineProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalHeight = rect.height;
      const start = windowHeight * 0.8;
      const end = -totalHeight + windowHeight * 0.5;
      const scrolled = start - rect.top;
      const range = start - end;
      const progress = Math.max(0, Math.min(1, scrolled / range));
      setLineProgress(progress);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section ref={containerRef} className="py-24 bg-background relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-16">
          <span className="font-mono text-xs tracking-widest text-primary font-medium uppercase mb-3 block">
            Our Litigation Process
          </span>
          <h2 className="font-serif text-3xl md:text-5xl font-bold tracking-tight text-foreground">
            A Disciplined Approach to Business Disputes
          </h2>
          <div className="w-16 h-px bg-secondary mt-6" />
        </div>

        <div className="relative">
          {steps.map((step, index) => <ProcessStep key={index} step={step} index={index} isLast={index === steps.length - 1} />)}
        </div>
      </div>
    </section>;
}